import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, CheckCircle, Phone, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';

const LandingPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPage = async () => {
      try {
        const response = await api.getLandingPage(slug);
        setPage(response.data);
      } catch (error) {
        console.error('Error loading landing page:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPage();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-dark flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-primary-dark flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Page Not Found</h1>
          <Link to="/" className="text-electric-teal hover:underline">
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-dark" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img 
            src={page.image_url}
            alt={page.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark via-primary-dark/90 to-primary-dark/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <span className="inline-block px-4 py-2 bg-electric-teal/20 text-electric-teal font-bold uppercase tracking-wider text-sm rounded-full mb-6">
              Special Offer
            </span>
            
            <h1 className="text-5xl md:text-7xl font-black text-white tracking-tighter leading-none mb-6">
              {page.headline}
            </h1>
            
            <p className="text-2xl text-electric-teal font-bold mb-6">
              {page.subheadline}
            </p>

            {/* Benefits */}
            <ul className="space-y-3 mb-8">
              {page.benefits?.map((benefit, index) => (
                <li key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-electric-teal flex-shrink-0" />
                  <span className="text-white/80 text-lg">{benefit}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={page.cta_link || '/book-estimate'} data-testid="landing-cta-btn">
                <Button className="bg-electric-teal text-primary-dark font-bold uppercase tracking-wider px-8 py-6 text-lg hover:bg-white transition-all btn-glow">
                  <Zap className="w-5 h-5 mr-2" />
                  {page.cta_text || 'Get Free Quote'}
                </Button>
              </Link>
              <a href="tel:7789602377" data-testid="landing-call-btn">
                <Button variant="outline" className="border-white/30 text-white font-bold uppercase tracking-wider px-8 py-6 text-lg hover:bg-white/10 hover:border-electric-teal transition-all">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="bg-electric-teal py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-primary-dark font-black text-3xl">15+</p>
              <p className="text-primary-dark/70 font-bold text-sm uppercase">Years Experience</p>
            </div>
            <div>
              <p className="text-primary-dark font-black text-3xl">2,500+</p>
              <p className="text-primary-dark/70 font-bold text-sm uppercase">Projects Completed</p>
            </div>
            <div>
              <p className="text-primary-dark font-black text-3xl">100%</p>
              <p className="text-primary-dark/70 font-bold text-sm uppercase">Licensed & Insured</p>
            </div>
            <div>
              <p className="text-primary-dark font-black text-3xl">5.0</p>
              <p className="text-primary-dark/70 font-bold text-sm uppercase">Star Rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-secondary-dark">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Don't Wait - Limited Time Offer!
          </h2>
          <p className="text-white/70 text-lg mb-8">
            Schedule your free estimate today and take advantage of this special promotion.
          </p>
          <Link to={page.cta_link || '/book-estimate'}>
            <Button className="bg-electric-teal text-primary-dark font-bold uppercase tracking-wider px-12 py-6 text-xl hover:bg-white transition-all btn-glow">
              {page.cta_text || 'Get Your Free Quote'}
              <ArrowRight className="w-6 h-6 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Minimal Footer */}
      <footer className="py-8 bg-primary-dark border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Island Coastal Electric. All rights reserved. | Licensed & Insured
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
